import type { AsyncDataOptions, NuxtApp } from '#app'
import type {
  CmsFooterQuery,
  CmsPaymentIconsQuery,
  Page,
  Seo,
  TrustCertificatesCollection,
  TrustTeaserCarouselsCollection,
} from '~~/@types/generated/cms/graphql-schema-types'
import type {
  CustomFaqLotteries,
  CustomFaqScratchcards,
} from '~~/layers/content/types'

import * as fs from 'fs'
import path from 'path'

type Options<T> = Pick<AsyncDataOptions<T>, 'immediate' | 'lazy'>

const isExpired = (expirationDate: number) =>
  new Date(expirationDate).getTime() < Date.now()

const getCachedData = (key: string, nuxtApp: NuxtApp) => {
  const data = nuxtApp.payload.data[key] || nuxtApp.static.data[key]
  if (data && isExpired(data.expirationDate)) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete nuxtApp.payload.data[key]
      return
    } catch {
      /* empty */
    }
  }
  if (!data) return
  return data
}

const transform = <T>(input: T) => {
  const DAYS_UNTIL_EXPIRATION = 7
  const now = new Date()
  const expirationDate = now.setDate(now.getDate() + DAYS_UNTIL_EXPIRATION)

  return { ...input, expirationDate }
}

export const useContent = () => {
  const licensedTerritory = useLicensedTerritory({ case: 'snake' })
  const { currentLocale } = useLanguageUtils()

  const currentLocaleSnake = computed(() =>
    currentLocale.value.replace(/-/g, '_'),
  )

  const { log: datadogLog } = useDatadog()
  const {
    app: { cdnURL },
  } = useRuntimeConfig()
  const isServer = getIsServer()

  const getBaseUrl = () => {
    const basePath = isServer ? path.join(process.cwd(), 'public') : cdnURL
    return `${basePath}/__content__`
  }

  const getContentById = <ReturnType>(
    collection: string,
    id: string,
    options: Options<ReturnType> = {},
  ) => {
    const filePath = `${getBaseUrl()}/${licensedTerritory}/${currentLocaleSnake.value}/${collection}/${id}.json`

    return useAsyncData(
      `${collection}/${id}/${licensedTerritory}/${currentLocaleSnake.value}`,
      (_nuxtApp, { signal }) => {
        if (isServer) {
          try {
            const data = fs.readFileSync(filePath, 'utf-8')
            return Promise.resolve(JSON.parse(data) as ReturnType)
          } catch (error) {
            datadogLog('error', {
              component: 'useContent.ts',
              error,
              message: `[content] Failed to getContentById(${collection})`,
            })
          }
        }

        return $fetch(filePath, { signal })
          .catch((error) => {
            datadogLog('error', {
              component: 'useContent.ts',
              error,
              message: `[content] Failed to getContentById(${collection}, ${id})`,
            })
          })
          .then((data) => data as ReturnType)
      },
      {
        getCachedData,
        lazy: getIsClient(),
        transform: transform<ReturnType>,
        ...options,
      },
    )
  }

  const getUniqueContent = <ReturnType>(
    collection: string,
    withLicensedTerritory = true,
  ) => {
    const territoryPath = withLicensedTerritory ? `${licensedTerritory}/` : ''
    const filePath = `${getBaseUrl()}/${territoryPath}${currentLocaleSnake.value}/${collection}.json`

    return useAsyncData(
      `${collection}/${licensedTerritory}/${currentLocaleSnake.value}`,
      (_nuxtApp, { signal }) => {
        if (isServer) {
          try {
            const data = fs.readFileSync(filePath, 'utf-8')
            return Promise.resolve(JSON.parse(data) as ReturnType)
          } catch (error) {
            datadogLog('error', {
              component: 'useContent.ts',
              error,
              message: `[content] Failed to getUniqueContent(${collection})`,
            })
          }
        }

        return $fetch(filePath, { signal })
          .catch((error) => {
            datadogLog('error', {
              component: 'useContent.ts',
              error,
              message: `[content] Failed to getUniqueContent(${collection})`,
            })
          })
          .then((data) => data as ReturnType)
      },
      {
        getCachedData,
        lazy: getIsClient(),
        transform: transform<ReturnType>,
      },
    )
  }

  const getFaq = (id: string, options?: Options<Page>) =>
    getContentById<CustomFaqLotteries | CustomFaqScratchcards>(
      'faqs',
      id,
      options,
    )

  const getFooter = () =>
    getUniqueContent<CmsFooterQuery['footerCollection']>('footer')

  const getPage = (id: string, options?: Options<Page>) => {
    const normalizedId = id.startsWith('/') ? id.slice(1) : id
    return getContentById<Page>('pages', normalizedId, options)
  }

  const getPaymentIcons = () =>
    getUniqueContent<CmsPaymentIconsQuery['paymentIconCollection']>(
      'payment-icons',
    )

  const getPrizes = (id: string, options?: Options<Page>) =>
    getContentById<Page>('prizes', id, options)

  const getRules = (id: string, options?: Options<Page>) =>
    getContentById<Page>('rules', id, options)

  const getSeo = () => getUniqueContent<{ items: Seo[] }>('seo')

  const getTrustTeaserCarousels = () =>
    getUniqueContent<TrustTeaserCarouselsCollection>('trust-teaser-carousels')

  const getTrustCertificates = () =>
    getUniqueContent<TrustCertificatesCollection>('trust-certificates')

  return {
    getFaq,
    getFooter,
    getPage,
    getPaymentIcons,
    getPrizes,
    getRules,
    getSeo,
    getTrustCertificates,
    getTrustTeaserCarousels,
  }
}
